import React, {useEffect, useMemo, useState} from "react";
import {Modal, ModalBody, ModalHeader, Progress} from "reactstrap";
import {ItemQuantity} from "../State/Inventory";
import {randomInt} from "../Utility/utilities";
import {GiPineTree, GiWarPick} from "react-icons/gi";
import crackedBoulder from "../Assets/cracked-boulder.svg";
import {
    TREE_RESOURCES,
    TREE_ICON_SIZE,
    BOULDER_ICON_SIZE,
    PatchUnlockModalProps,
    TreeState,
    TreeChopGameProps,
    CrackState,
    BoulderCrackGameProps,
    LootResult,
    getTargetCount,
    getHitsPerTarget,
    getProgressKey,
} from "./patchUnlockLogic";
import {
    GameWrapper,
    TreeCard,
    Boulder,
    CrackCard,
    CrackImage,
    LootBadge,
} from "./PatchUnlockStyles";

export function PatchUnlockModal({resourceType, clearedSpots, isOpen, onClose, onComplete, treeHitBonus = 0, enableTreeHold, enableBoulderHold}: PatchUnlockModalProps) {
  const isTreeGame = TREE_RESOURCES.includes(resourceType);
  const patchNumber = clearedSpots + 1;
  const targetCount = useMemo(() => getTargetCount(clearedSpots), [clearedSpots]);
  const hitsPerTarget = useMemo(() => getHitsPerTarget(clearedSpots), [clearedSpots]);

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" centered>
      <ModalHeader toggle={onClose}>Clear the next {resourceType} patch</ModalHeader>
      <ModalBody>
        <p>
          Patch {patchNumber} has {targetCount} obstacle{targetCount > 1 ? "s" : ""} and each needs {hitsPerTarget} clicks to clear.
          {isTreeGame ? " Chop the trees for wood rewards." : " Chip away at the cracks until the boulder crumbles."}
        </p>
        {isTreeGame ? (
          <TreeChopGame
            resourceType={resourceType}
            treeCount={targetCount}
            hitsPerTree={hitsPerTarget}
            hitBonus={treeHitBonus}
            holdEnabled={!!enableTreeHold}
            onComplete={(rewards) => {
              onComplete(rewards);
              onClose();
            }}
          />
        ) : (
          <BoulderCrackGame
            resourceType={resourceType}
            crackCount={targetCount}
            hitsPerCrack={hitsPerTarget}
            holdEnabled={!!enableBoulderHold}
            onComplete={(rewards) => {
              onComplete(rewards);
              onClose();
            }}
          />
        )}
      </ModalBody>
    </Modal>
  );
}

function TreeChopGame({resourceType, treeCount, hitsPerTree, hitBonus, holdEnabled, onComplete}: TreeChopGameProps) {
  const progressKey = useMemo(() => getProgressKey(resourceType), [resourceType]);
  const buildDefaultTrees = React.useCallback(() =>
    Array.from({length: treeCount}, (_, idx) => ({
      id: idx,
      hitsRemaining: hitsPerTree,
      flashing: false,
      broken: false,
    })), [treeCount, hitsPerTree]);

  const loadTrees = React.useCallback(() => {
    const stored = localStorage.getItem(progressKey);
    if (!stored) return {trees: buildDefaultTrees(), finished: false};
    try {
      const parsed = JSON.parse(stored);
      if (parsed?.mode !== "trees" || parsed?.hitsPerTarget !== hitsPerTree) {
        localStorage.removeItem(progressKey);
        return {trees: buildDefaultTrees(), finished: false};
      }
      if (!Array.isArray(parsed.trees) || parsed.trees.length !== treeCount) {
        localStorage.removeItem(progressKey);
        return {trees: buildDefaultTrees(), finished: false};
      }
      return {
        trees: parsed.trees.map((tree: { id: number; hitsRemaining: number; broken: boolean }) => ({
          id: tree.id,
          hitsRemaining: tree.hitsRemaining,
          flashing: false,
          broken: tree.broken,
        })),
        finished: !!parsed.finished,
      };
    } catch (error) {
      console.error("Failed to load tree progress", error);
      return {trees: buildDefaultTrees(), finished: false};
    }
  }, [buildDefaultTrees, hitsPerTree, progressKey, treeCount]);

  const [{trees, finished}, setTreeState] = useState<{ trees: TreeState[]; finished: boolean }>(loadTrees);
  const woodEarned = useMemo(() => trees.filter(tree => tree.broken).length * 4, [trees]);
  const [heldTreeId, setHeldTreeId] = useState<number | null>(null);

  useEffect(() => {
    setTreeState(loadTrees());
  }, [loadTrees]);

  useEffect(() => {
    localStorage.setItem(progressKey, JSON.stringify({
      mode: "trees",
      hitsPerTarget: hitsPerTree,
      trees: trees.map(tree => ({id: tree.id, hitsRemaining: tree.hitsRemaining, broken: tree.broken})),
      finished,
    }));
  }, [progressKey, trees, hitsPerTree, finished]);

  useEffect(() => {
    if (!holdEnabled || heldTreeId === null) return;
    const interval = setInterval(() => handleTreeClick(heldTreeId), 180);
    return () => clearInterval(interval);
  }, [holdEnabled, heldTreeId]);

  const handleTreeClick = (treeId: number) => {
    if (finished) return;
    setTreeState(prev => {
      let woodGained = 0;
      const nextTrees = prev.trees.map(tree => {
        if (tree.id !== treeId || tree.broken) return tree;
        const remaining = tree.hitsRemaining - (1 + hitBonus);
        const broken = remaining <= 0;
        if (broken) woodGained += 4;
        return {
          ...tree,
          hitsRemaining: Math.max(0, remaining),
          flashing: true,
          broken,
        };
      });
      const allBroken = nextTrees.every(tree => tree.hitsRemaining === 0);
      const updatedWood = woodEarned + woodGained;
      if (allBroken) {
        onComplete(updatedWood > 0 ? [{itemId: "wood", quantity: updatedWood}] : []);
        localStorage.removeItem(progressKey);
      }
      return {trees: nextTrees, finished: allBroken};
    });
    setTimeout(() => {
      setTreeState(prev => ({...prev, trees: prev.trees.map(tree => ({...tree, flashing: false}))}));
    }, 250);
  };

  return (
    <GameWrapper>
      {trees.map(tree => (
        <TreeCard
          key={tree.id}
          $broken={tree.broken}
          $flash={tree.flashing}
          onClick={() => handleTreeClick(tree.id)}
          onMouseDown={() => holdEnabled && setHeldTreeId(tree.id)}
          onMouseUp={() => setHeldTreeId(null)}
          onMouseLeave={() => setHeldTreeId(null)}
        >
          <GiPineTree size={TREE_ICON_SIZE} />
          <div>{tree.broken ? "Stump cleared" : "Keep chopping"}</div>
          <Progress value={(1 - tree.hitsRemaining / hitsPerTree) * 100} style={{width: "100%"}} />
        </TreeCard>
      ))}
    </GameWrapper>
  );
}

function BoulderCrackGame({resourceType, crackCount, hitsPerCrack, holdEnabled, onComplete}: BoulderCrackGameProps) {
  const progressKey = useMemo(() => getProgressKey(resourceType), [resourceType]);
  const buildDefaultCracks = React.useCallback(() =>
    Array.from({length: crackCount}, (_, idx) => ({
      id: idx,
      hitsRemaining: hitsPerCrack,
      flashing: false,
      complete: false,
    })), [crackCount, hitsPerCrack]);

  const loadCracks = React.useCallback(() => {
    const stored = localStorage.getItem(progressKey);
    if (!stored) return {cracks: buildDefaultCracks(), loot: [], finished: false};
    try {
      const parsed = JSON.parse(stored);
      if (parsed?.mode !== "boulders" || parsed?.hitsPerTarget !== hitsPerCrack) {
        localStorage.removeItem(progressKey);
        return {cracks: buildDefaultCracks(), loot: [], finished: false};
      }
      if (!Array.isArray(parsed.cracks) || parsed.cracks.length !== crackCount) {
        localStorage.removeItem(progressKey);
        return {cracks: buildDefaultCracks(), loot: [], finished: false};
      }
      return {
        cracks: parsed.cracks.map((crack: { id: number; hitsRemaining: number; complete: boolean }) => ({
          id: crack.id,
          hitsRemaining: crack.hitsRemaining,
          flashing: false,
          complete: crack.complete,
        })),
        loot: Array.isArray(parsed.loot) ? parsed.loot : [],
        finished: !!parsed.finished,
      };
    } catch (error) {
      console.error("Failed to load boulder progress", error);
      return {cracks: buildDefaultCracks(), loot: [], finished: false};
    }
  }, [buildDefaultCracks, crackCount, hitsPerCrack, progressKey]);

  const [{cracks, loot, finished}, setCrackState] = useState<{ cracks: CrackState[]; loot: LootResult[]; finished: boolean }>(
    loadCracks
  );
  const [heldCrackId, setHeldCrackId] = useState<number | null>(null);

  useEffect(() => {
    setCrackState(loadCracks());
  }, [loadCracks]);

  useEffect(() => {
    localStorage.setItem(progressKey, JSON.stringify({
      mode: "boulders",
      hitsPerTarget: hitsPerCrack,
      cracks: cracks.map(crack => ({id: crack.id, hitsRemaining: crack.hitsRemaining, complete: crack.complete})),
      loot,
      finished,
    }));
  }, [progressKey, cracks, loot, hitsPerCrack, finished]);

  useEffect(() => {
    if (!holdEnabled || heldCrackId === null) return;
    const interval = setInterval(() => handleCrackClick(heldCrackId), 180);
    return () => clearInterval(interval);
  }, [holdEnabled, heldCrackId]);

  const handleCrackClick = (crackId: number) => {
    if (finished) return;
    setCrackState(prev => {
      const next = prev.cracks.map(crack => {
        if (crack.id !== crackId || crack.complete) return crack;
        const remaining = crack.hitsRemaining - 1;
        return {
          ...crack,
          hitsRemaining: Math.max(0, remaining),
          flashing: true,
          complete: remaining <= 0,
        };
      });
      const allComplete = next.every(crack => crack.complete);
      if (allComplete) {
        const rewards: LootResult[] = [];
        if (Math.random() < 0.25) {
          rewards.push({itemId: "coal", quantity: randomInt(10, 35), label: "Coal"});
        }
        if (Math.random() < 0.25) {
          rewards.push({itemId: "stone", quantity: randomInt(10, 35), label: "Stone"});
        }
        localStorage.removeItem(progressKey);
        setCrackState({cracks: next, loot: rewards, finished: true});
        onComplete(rewards.map(({itemId, quantity}) => ({itemId, quantity})));
        return {cracks: next, loot: rewards, finished: true};
      }
      return {cracks: next, loot: prev.loot, finished: false};
    });
    setTimeout(() => {
      setCrackState(prev => ({...prev, cracks: prev.cracks.map(crack => ({...crack, flashing: false}))}));
    }, 250);
  };

  const totalProgress = (cracks.filter(c => c.complete).length / cracks.length) * 100;

  return (
    <GameWrapper>
      <Boulder $flash={cracks.some(crack => crack.flashing)} onClick={() => {}} aria-label="Boulder">
        <GiWarPick size={BOULDER_ICON_SIZE} />
        <div>Click each crack to shatter the rock.</div>
        <Progress value={totalProgress} style={{width: "100%"}} />
        {loot.map(reward => (
          <LootBadge key={reward.itemId}>
            +{reward.quantity} {reward.label}
          </LootBadge>
        ))}
      </Boulder>
      {cracks.map(crack => (
        <CrackCard
          key={crack.id}
          $complete={crack.complete}
          $flash={crack.flashing}
          onClick={() => handleCrackClick(crack.id)}
          onMouseDown={() => holdEnabled && setHeldCrackId(crack.id)}
          onMouseUp={() => setHeldCrackId(null)}
          onMouseLeave={() => setHeldCrackId(null)}
        >
          <CrackImage src={crackedBoulder} alt="Cracked boulder" />
          <div>{crack.complete ? "Boulder cleared" : "Chip away"}</div>
          <Progress value={(1 - crack.hitsRemaining / hitsPerCrack) * 100} style={{width: "100%"}} />
        </CrackCard>
      ))}
    </GameWrapper>
  );
}
